import { AgendaDestinatarios, PersonaDetalle } from "../schema/person.schema";
import {
  Box,
  Button,
  Checkbox,
  CheckboxCheckedChangeDetails,
  Flex,
  Table,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { v4 } from "uuid";
import { AppColors } from "@/styles/colors";
import { NoDataSmall } from "@/components/common/section-components/NoDataSmall";
import { lazy, Suspense, useState } from "react";
import { Unlink } from "lucide-react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import {
  unlinkBeneficiaries,
  UnlinkBeneficiariesProps,
} from "@/services/persons.service";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";

interface Props {
  personData: Partial<PersonaDetalle> | null;
  fetchPersonDetail: (id: number) => Promise<void>;
}

export const PersonBeneficiaryAgenda = ({
  personData,
  fetchPersonDetail,
}: Props) => {
  const [selection, setSelection] = useState<number[]>([]);

  const [loadingUnlinkBeneficiary, setLoadingUnlinkBeneficiary] =
    useState<boolean>(false);

  const { fetchPermissions } = useRoles();

  const navigate = useNavigate();

  const handleItemCheckChange = (
    changes: CheckboxCheckedChangeDetails,
    item: AgendaDestinatarios,
  ) => {
    setSelection((prev) => {
      return changes.checked
        ? [...prev, item?.id]
        : selection.filter((id) => {
            return id !== item?.id;
          });
    });
  };

  const handleUnlinkBeneficiary = async () => {
    setLoadingUnlinkBeneficiary(true);
    try {
      const objToSend: UnlinkBeneficiariesProps = {
        destinatarioIds: selection,
        idPersona: Number(personData?.id),
      };

      await unlinkBeneficiaries(objToSend);

      toaster.create({
        type: "success",
        title: "Cuentas seleccionadas desasociadas con éxito.",
      });

      setSelection([]);
      await fetchPersonDetail(Number(personData?.id));

      setOpenUnlink(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)),
        {
          tab: "destinatarios",
        },
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "No se pudo desasociar las cuentas seleccionadas.",
        });
      }

      throw error;
    } finally {
      setLoadingUnlinkBeneficiary(false);
      setOpenUnlink(false);
    }
  };

  const { open: openUnlink, setOpen: setOpenUnlink } = useDisclosure();

  const getIdentifier = (item: AgendaDestinatarios) => {
    if (item?.type === "internal" && !item?.cbu) return "Cooperativa";

    if (!item?.cbu && item?.type === "external") return "-";
    if (item?.cbu?.startsWith("0000")) {
      return "CVU";
    } else {
      return "CBU";
    }
  };

  const { userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.PERSONS && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column">
      {hasWriteAccess && (
        <Flex justifyContent="end" padding="16px" gap="8px">
          <Button
            variant="solid"
            paddingX="8px"
            disabled={!selection?.length}
            onClick={() => setOpenUnlink(true)}
          >
            <Unlink />
            <Text>Desasociar destinatario{selection?.length > 1 && "s"}</Text>
          </Button>
        </Flex>
      )}

      {personData?.destinatarios?.length ? (
        <Box paddingTop="12px" borderRadius="12px">
          <Table.ScrollArea
            css={{
              "@media (max-width: 1350px) and (min-width: 1272px)": {
                maxWidth: "900px",
                height: "220px",
              },
              "@media (max-width: 1414px) and (min-width: 1351px)": {
                maxWidth: "1100px",
                height: "300px",
              },
              "@media (max-width: 1590px) and (min-width: 1415px)": {
                maxWidth: "1250px",
                height: "365px",
              },
              "@media (max-width: 1696px) and (min-width: 1591px)": {
                maxWidth: "1350px",
                height: "412px",
              },
              "@media (max-width: 1900px)": {
                maxWidth: "1500px",
                height: "505px",
              },
              "@media (min-width: 1901px)": {
                height: "505px",
              },
            }}
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Table.Root size="sm" striped stickyHeader borderRadius="12px">
              <Table.Header>
                <Table.Row>
                  {hasWriteAccess && (
                    <Table.ColumnHeader w="6">
                      <Text
                        padding="16px"
                        color={AppColors.GRAY_TEXT}
                        fontWeight={700}
                        textTransform="uppercase"
                        textAlign="center"
                        fontSize="12px"
                      >
                        SELECCIONAR
                      </Text>
                    </Table.ColumnHeader>
                  )}

                  <Table.ColumnHeader>
                    <Text
                      padding="16px"
                      color={AppColors.GRAY_TEXT}
                      fontWeight={700}
                      textTransform="uppercase"
                      fontSize="12px"
                    >
                      Titular
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text
                      padding="16px"
                      color={AppColors.GRAY_TEXT}
                      fontWeight={700}
                      textTransform="uppercase"
                      fontSize="12px"
                    >
                      Banco
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text
                      padding="16px"
                      color={AppColors.GRAY_TEXT}
                      fontWeight={700}
                      textTransform="uppercase"
                      fontSize="12px"
                    >
                      Tipo de cuenta
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text
                      padding="16px"
                      color={AppColors.GRAY_TEXT}
                      fontWeight={700}
                      textTransform="uppercase"
                      fontSize="12px"
                    >
                      Identificador
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text
                      padding="16px"
                      color={AppColors.GRAY_TEXT}
                      fontWeight={700}
                      textTransform="uppercase"
                      fontSize="12px"
                    >
                      N° CUENTA/CBU/CVU
                    </Text>
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {personData?.destinatarios?.map((item, index) => (
                  <Table.Row key={v4()}>
                    {hasWriteAccess && (
                      <Table.Cell paddingX={"35px"} width={"100px"}>
                        <Checkbox.Root
                          top="1"
                          paddingLeft={3}
                          aria-label="Select row"
                          checked={selection.includes(item?.id)}
                          onCheckedChange={(changes) =>
                            handleItemCheckChange(changes, item)
                          }
                        >
                          <Checkbox.HiddenInput data-testid="row-selection-checkbox" />
                          <Checkbox.Control
                            borderColor={AppColors.GRAY_BORDER_1}
                            cursor="pointer"
                          />
                        </Checkbox.Root>
                      </Table.Cell>
                    )}

                    <Table.Cell>
                      <Text
                        data-testid={`person-beneficiary-agenda-link-${index}`}
                        padding="16px"
                        fontWeight={400}
                        fontSize="12px"
                        color={AppColors.BLACK_TEXT_SECONDARY}
                      >
                        {item?.holderName ?? "-"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        padding="16px"
                        fontWeight={400}
                        fontSize="12px"
                        color={AppColors.BLACK_TEXT_SECONDARY}
                      >
                        {item?.type === "external"
                          ? item?.bankName
                          : item?.cooperativeName}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        padding="16px"
                        fontWeight={400}
                        fontSize="12px"
                        color={AppColors.BLACK_TEXT_SECONDARY}
                      >
                        {item?.type === "external"
                          ? "-"
                          : (item?.accountType ?? "-")}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        padding="16px"
                        fontWeight={400}
                        fontSize="12px"
                        color={AppColors.BLACK_TEXT_SECONDARY}
                        data-testid={`beneficiary-agenda-identifier-${index}`}
                      >
                        {getIdentifier(item)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        padding="16px"
                        fontWeight={400}
                        fontSize="12px"
                        color={AppColors.BLACK_TEXT_SECONDARY}
                      >
                        {item?.type === "external"
                          ? item.cbu
                          : item?.accountNumber}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        </Box>
      ) : (
        <NoDataSmall />
      )}
      <Suspense>
        <UnlinkBeneficiaryModalDynamic
          openUnlinkBeneficiaries={openUnlink}
          setOpenUnlinkBeneficiaries={setOpenUnlink}
          beneficiariesToUnlink={selection?.length}
          handleAccept={handleUnlinkBeneficiary}
          loadingAccept={loadingUnlinkBeneficiary}
        />
      </Suspense>
    </Flex>
  );
};

const UnlinkBeneficiaryModalDynamic = lazy(() =>
  import("@/components/modal/UnlinkBeneficiaryModal").then((el) => ({
    default: el["UnlinkBeneficiaryModal"],
  })),
);
